import React from 'react';
import { useTranslation } from '../../utils/languageUtils';
import './CustomExportPanel.css';

// QP_Version: Pair Card Front + Card Back by the same index; Back uses fixed index (1).
// Box is shown independently.
const CustomExportPanel = ({
  exportedImages,
  showExportedImages,
  setShowExportedImages,
  allViewsImages = [],
  onAddToCart,
  isTestMode = true,
  isExporting = false,
  statusMessage = '',
  // Optional template config to map view IDs and limits
  templateConfig
}) => {
  const { t } = useTranslation();
  // Hooks must be declared before any early returns
  const [currentIndex, setCurrentIndex] = React.useState(1);
  // Step management: 'card' -> 'box'
  const [step, setStep] = React.useState('card');
  const [visitedCard, setVisitedCard] = React.useState(true);
  const [visitedBox, setVisitedBox] = React.useState(false);
  // Touch swipe ref must be declared before early returns (hook rule)
  const touchStartXRef = React.useRef(null);
  // Mouse drag ref for desktop swipe-like interaction
  const mouseStartXRef = React.useRef(null);
  if (!showExportedImages) return null;

  // Touch swipe: when on Card step, swipe left/right changes index only.
  // View change (Card -> Box) is by Next button, not swipe.
  const onCardSwipeStart = (e) => {
    // record start; action will only occur if step === 'card'
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };
  const onCardSwipeEnd = (e) => {
    if (step !== 'card') return; // only index-swipe on Card step
    const startX = touchStartXRef.current;
    const endX = e.changedTouches?.[0]?.clientX;
    if (startX == null || endX == null) return;
    const dx = endX - startX;
    const threshold = 40;
    if (dx <= -threshold) {
      // swipe left -> next index
      goNext();
    } else if (dx >= threshold) {
      // swipe right -> prev index
      goPrev();
    }
  };

  // Mouse drag handlers (desktop)
  const onCardMouseDown = (e) => {
    mouseStartXRef.current = e.clientX ?? null;
  };
  const onCardMouseUp = (e) => {
    if (step !== 'card') return; // only index-swipe on Card step
    const startX = mouseStartXRef.current;
    const endX = e.clientX;
    if (startX == null || endX == null) return;
    const dx = endX - startX;
    const threshold = 40;
    if (dx <= -threshold) {
      goNext();
    } else if (dx >= threshold) {
      goPrev();
    }
    mouseStartXRef.current = null;
  };

  // Normalize list
  const items = Array.isArray(allViewsImages) && allViewsImages.length > 0
    ? allViewsImages
    : (exportedImages ? [exportedImages] : []);

  const getViewId = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v.id) return v.id;
    return String(v);
  };

  // Heuristics + config mapping
  const mapByView = new Map();
  for (const it of items) {
    const vid = getViewId(it.view);
    if (!vid) continue;
    if (!mapByView.has(vid)) mapByView.set(vid, new Map());
    const byIdx = mapByView.get(vid);
    const idx = parseInt(it.designIndex || 1, 10) || 1;
    byIdx.set(idx, it);
  }

  const cfg = templateConfig || {};
  // Try to detect front/back/box if not configured
  const guessId = (keywords) => {
    const ids = Array.from(mapByView.keys());
    return ids.find(id => keywords.some(k => id.toLowerCase().includes(k))) || '';
  };
  const frontId = cfg.frontViewId || guessId(['front', 'cardfront', 'face']);
  const backId = cfg.backViewId || guessId(['back', 'cardback', 'rear']);
  const boxId = cfg.boxViewId || guessId(['box', 'case']);

  const frontMap = mapByView.get(frontId) || new Map();
  const backMap = mapByView.get(backId) || new Map();
  const boxMap = mapByView.get(boxId) || new Map();

  // Limits: front has many (e.g., 55). Back fixed to 1.
  const maxFront = cfg.maxFrontIndex || Math.max(1, ...Array.from(frontMap.keys(), k => Number(k) || 1));
  const backFixedIndex = cfg.backFixedIndex || 1;
  const clamp = (v) => Math.max(1, Math.min(maxFront || 1, v));
  const goPrev = () => setCurrentIndex(i => i > 1 ? i - 1 : (maxFront || 1));
  const goNext = () => setCurrentIndex(i => i < (maxFront || 1) ? i + 1 : 1);

  const frontItem = frontMap.get(clamp(currentIndex));
  const backItem = backMap.get(backFixedIndex) || backMap.get(clamp(currentIndex));
  const boxItem = boxMap.get(1) || boxMap.get(backFixedIndex) || Array.from(boxMap.values())[0];

  // Handlers for step navigation
  const hasBox = !!boxItem;
  const handleNextStep = () => {
    if (hasBox) {
      setStep('box');
      setVisitedBox(true);
    }
  };
  const handlePrevStep = () => {
    setStep('card');
    setVisitedCard(true);
  };

  // Add to Cart requires both steps viewed if box exists
  const canAddToCart = hasBox ? (visitedCard && visitedBox) : true;

  return (
    <>
      {/* Screen backdrop to block clicks outside and close on click */}
      <div
        className="cep-backdrop"
        role="presentation"
        aria-hidden="true"
      />

      <div
        className="custom-export-panel"
        onTouchStart={onCardSwipeStart}
        onTouchEnd={onCardSwipeEnd}
        onMouseDown={onCardMouseDown}
        onMouseUp={onCardMouseUp}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <div className="cep-header">
        <button className="cep-close" onClick={() => setShowExportedImages(false)}>×</button>
      </div>

      {/* Card step */}
      {step === 'card' && (
        <div className="cep-step">
          {/* Centered section title like screenshot */}
          
          <div className="cep-section-title">{t('export.cardPreviewTitle', '卡牌牌面预览')}</div>

          {/* Paired card previews (Front + Back) */}
          <div className="cep-grid">
            <div className="cep-preview">
              {frontItem?.preview ? (
                <img className="cep-img" src={frontItem.preview} alt="Card Front" />
              ) : (
                <div className="cep-placeholder" />
              )}
              <div className="cep-caption">{t('views.front') || 'Front'}</div>
            </div>
            <div className="cep-preview">
              {backItem?.preview ? (
                <img className="cep-img" src={backItem.preview} alt="Card Back" />
              ) : (
                <div className="cep-placeholder" />
              )}
              <div className="cep-caption">{t('views.back') || 'Back'}</div>
            </div>
          </div>

          {/* Centered pager below previews like "<1/55>" */}
          <div className="cep-pager-inline">
            <button className="cep-pager-ghost" onClick={goPrev} aria-label="prev">‹</button>
            <div className="cep-pager-text">{`${(frontItem?.designIndex ?? currentIndex)}/${maxFront || 1}`}</div>
            <button className="cep-pager-ghost" onClick={goNext} aria-label="next">›</button>
          </div>

          {/* Swipe left/right to switch steps when box exists */}
        </div>
      )}

      {/* Box step */}
      {step === 'box' && hasBox && (
        <div className="cep-step">
          <div className="cep-section-title">{t('export.boxPreviewTitle', '牌盒预览')}</div>
          <div className="cep-grid">
            {/* Box Preview (product + design) */}
            <div className="cep-preview">
              {boxItem?.preview ? (
                <img className="cep-img cep-box-img" src={boxItem.preview} alt="Box Preview" />
              ) : (
                <div className="cep-placeholder cep-box-placeholder" />
              )}
              <div className="cep-caption">{t('export.preview') || 'Preview'}</div>
            </div>

            {/* Box Design (design-only transparent PNG) */}
            <div className="cep-preview">
              {boxItem?.design ? (
                <div className="cep-checker">
                  <img className="cep-img cep-box-img" src={boxItem.design} alt="Box Design" />
                </div>
              ) : (
                <div className="cep-placeholder cep-box-placeholder" />
              )}
              <div className="cep-caption">{t('export.design') || 'Design'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Footer: Next on first step (if box exists); Back + Add to Cart on last step; Add to Cart only when no box */}
      <div className="cep-footer">
        {step === 'card' && hasBox && (
          <button
            className="cep-primary"
            onClick={handleNextStep}
            disabled={isExporting}
          >
            {t('general.next', 'Next')}
          </button>
        )}
        {step === 'box' && hasBox && (
          <>
            <button
              className="cep-back"
              onClick={handlePrevStep}
              disabled={isExporting}
            >
              {t('general.back', 'Back')}
            </button>
            {typeof onAddToCart === 'function' && (
              <button
                className="cep-primary"
                onClick={onAddToCart}
                disabled={isExporting || !canAddToCart}
              >
                {t('export.addToCart', 'Add to Cart')}
              </button>
            )}
          </>
        )}
        {!hasBox && typeof onAddToCart === 'function' && (
          <button
            className="cep-primary"
            onClick={onAddToCart}
            disabled={isExporting || !canAddToCart}
          >
            {t('export.addToCart', 'Add to Cart')}
          </button>
        )}
      </div>

        {/* Blocking overlay while exporting */}
        {isExporting && (
          <div className="cep-overlay">
            {statusMessage || t('status.preparingExport')}
          </div>
        )}
      </div>
    </>
  );
};

export default CustomExportPanel;
